//  card-payments.js
//  Projecting the NEXT credit-card payment from the card's live balance.
//
//  runway.js projects every recurring series at its historical mean. For most
//  bills that's right — rent doesn't care what you spent last month. For a card
//  payment it is the one case where the mean is knowably wrong, because the
//  amount isn't a habit, it's a consequence:
//
//      payments:  $250, $1,800, $600, $2,400   -> mean $1,262
//      card owed right now:                       $2,400
//
//  Projecting $1,262 against a $2,400 statement understates the coming cash
//  outflow by half. We already sync the card's owed ledger balance, so for a
//  user who pays in full the next payment is close to *known*, not estimated.
//
//  That override is only safe for people who actually pay in full. A revolver
//  paying $200/month against a $6,000 balance would get a catastrophically
//  wrong $6,000 outflow projected. So each bank->card route is classified
//  first, and only 'payoff' routes get the balance-sized amount.
//
//  Classification uses an aggregate invariant rather than trying to line up
//  statement cycles (whose close/due offsets we don't have): over a long enough
//  window a card's owed balance changes by (purchases - payments), so a user
//  whose payments sum to roughly what they charged is paying it off, whatever
//  the per-month timing looks like. That's robust to a payment landing on the
//  1st vs the 28th, which per-cycle matching is not.
//
//  IMPORTANT: only the FIRST upcoming occurrence is balance-sized. The current
//  owed balance says nothing about a payment three months out — those charges
//  haven't been made yet — so later occurrences revert to the mean.
//
//  IN-FLIGHT MONEY: a card's owed balance is only a fair estimate of the coming
//  outflow while no payment is on its way to it. The moment cash leaves the
//  bank the two sides fall out of step — the card isn't credited for a day or
//  three, so it still reports the full amount owed, while the cash balance has
//  already dropped. Sizing the next payment off that stale figure charges the
//  user for the same money twice: once in the balance the runway starts from,
//  once in the payment it projects. Callers pass what's in flight per card
//  (in-flight.js finds it) and it comes off `owed` before anything is sized.

import { detectTransferPairs } from './transfers.js';
import { nextOccurrence } from './recurrence.js';
import { seriesKeyOf } from './series-key.js';
import { isSpendingType, spendDelta } from '../txType.js';

const DAY_MS = 24 * 60 * 60 * 1000;

// Payments summing to ~everything charged = paying it off. The band is wide
// because the window rarely starts and ends on a statement boundary, so one
// partial cycle at each end skews the ratio a few points either way.
const PAYOFF_MIN_COVERAGE = 0.85;
const PAYOFF_MAX_COVERAGE = 1.25;

// Same amount every month regardless of what was charged — an autopay-minimum
// or a fixed-installment plan. The mean is already the right projection.
const FIXED_MAX_CV = 0.15;

// Two payments give one interval — enough to place the next date, not enough to
// trust a coverage ratio. Three is the same floor detectRecurringCandidates uses.
const MIN_PAYMENTS_FOR_MODE = 3;

// Map an observed median interval onto the cadence vocabulary. Stepping by raw
// day counts would drift a monthly payment off its day-of-month over a 60-day
// horizon; nextOccurrence() handles calendar months properly.
const INTERVAL_CADENCES = [
  { type: 'weekly', min: 6, max: 8 },
  { type: 'biweekly', min: 12, max: 17 },
  { type: 'monthly', min: 26, max: 35 },
];

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function startOfDay(value) {
  const d = parseDate(value);
  return d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null;
}

function addDays(date, n) {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  d.setDate(d.getDate() + n);
  return d;
}

const round2 = (n) => parseFloat(n.toFixed(2));

function median(values) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function coefficientOfVariation(values) {
  if (values.length < 2) return 0;
  const mean = values.reduce((s, v) => s + v, 0) / values.length;
  if (mean <= 0) return 0;
  const varc = values.reduce((s, v) => s + (v - mean) * (v - mean), 0) / values.length;
  return Math.sqrt(varc) / mean;
}

function cadenceForInterval(days) {
  const hit = INTERVAL_CADENCES.find((c) => days >= c.min && days <= c.max);
  return hit ? hit.type : null;
}

// Owed is stored positive-when-owed (see App.jsx cardSummaries). A negative
// ledger means either an overpaid card (owe nothing) or an inverted upstream
// convention we shouldn't guess at — both resolve to "no balance to size from".
function owedOf(account) {
  const ledger = Number(account?.balance?.ledger);
  if (!Number.isFinite(ledger) || ledger <= 0) return 0;
  return ledger;
}

/**
 * Group detected transfers into bank->card payment routes and classify how the
 * user pays each card.
 *
 * @param {Array} transactions  ALL transactions (cash and card)
 * @param {Array} accounts      accounts, needing `id`, `type`, `balance.ledger`
 * @param {Object} [options]
 * @param {Date}   [options.today]
 * @param {number} [options.lookbackMonths=6]  window for the coverage ratio
 * @param {Object} [options.pairs]             precomputed detectTransferPairs() result
 * @param {Object} [options.inFlightByCard]    {cardAccountId: amount} already sent
 *                                             but not yet credited to the card
 * @param {Object} [options.unpairedPaymentsByCard] {cardAccountId: amount} paid
 *                                             but with one leg still settling,
 *                                             so absent from the matched pairs
 * @returns {Array<Object>} routes, most-recently-used first
 */
export function buildPaymentRoutes(transactions, accounts, options = {}) {
  const {
    today = new Date(),
    lookbackMonths = 6,
    pairs: precomputed = null,
    inFlightByCard = {},
    unpairedPaymentsByCard = {},
  } = options;

  const cardById = new Map(
    (accounts || []).filter((a) => a && a.type === 'credit').map((a) => [a.id, a]),
  );
  if (cardById.size === 0) return [];

  const { pairs } = precomputed || detectTransferPairs(transactions || []);
  const since = addDays(startOfDay(today) || new Date(), -Math.round(lookbackMonths * 30.436875));

  // A payment is a transfer whose destination is one of the user's cards.
  const byRoute = new Map();
  pairs.forEach((p) => {
    if (!cardById.has(p.toAccountId)) return;
    const key = `${p.fromAccountId}->${p.toAccountId}`;
    if (!byRoute.has(key)) {
      byRoute.set(key, {
        key,
        cashAccountId: p.fromAccountId,
        cardAccountId: p.toAccountId,
        payments: [],
        seriesKeys: new Set(),
      });
    }
    const route = byRoute.get(key);
    route.payments.push({
      id: p.outflowId,
      date: startOfDay(p.outflowDate),
      amount: p.amount,
      lagDays: p.lagDays,
    });
    // The runway runs on CASH transactions, so the series the ledger will have
    // built for this payment is keyed off the cash-side leg's description.
    route.seriesKeys.add(seriesKeyOf(p.outflow));
  });

  // Card purchases per card over the window — the denominator of the coverage
  // ratio. Payments are excluded: on the card they post as 'repayment'. Refunds
  // net off, because a returned item is not on the statement the next payment
  // has to cover.
  const spendByCard = new Map();
  (transactions || []).forEach((t) => {
    if (!t || !isSpendingType(t.type) || !cardById.has(t.accountId)) return;
    const d = startOfDay(t.date);
    if (!d || d < since) return;
    spendByCard.set(t.accountId, (spendByCard.get(t.accountId) || 0) + spendDelta(t));
  });

  const routes = [];
  byRoute.forEach((route) => {
    const payments = route.payments.sort((a, b) => a.date - b.date);
    const inWindow = payments.filter((p) => p.date >= since);
    const amounts = payments.map((p) => p.amount);

    const intervals = [];
    for (let i = 1; i < payments.length; i++) {
      intervals.push(Math.round((payments[i].date - payments[i - 1].date) / DAY_MS));
    }
    const medianIntervalDays = intervals.length ? median(intervals) : null;

    const cardSpend = spendByCard.get(route.cardAccountId) || 0;
    const totalPaid = inWindow.reduce((s, p) => s + p.amount, 0);
    // `payments` is built from matched pairs, so a payment with one leg still
    // settling is missing from it. Left uncorrected the coverage ratio reads
    // that gap as a payment the user never made and can relabel a payoff user
    // as a revolver — switching balance-sizing off for the day or two the two
    // legs are out of step, which is exactly when it matters most. The money
    // has moved; count it.
    const unpairedPaid = Math.max(0, Number(unpairedPaymentsByCard[route.cardAccountId]) || 0);
    const coverageRatio = cardSpend > 0 ? (totalPaid + unpairedPaid) / cardSpend : null;
    const amountCv = coefficientOfVariation(amounts);

    // Order matters: a payoff user with steady spend also shows a lowish CV, so
    // the coverage test — which is the one that actually describes payoff
    // behaviour — has to run first.
    let mode = 'unknown';
    if (payments.length >= MIN_PAYMENTS_FOR_MODE && coverageRatio != null) {
      if (coverageRatio >= PAYOFF_MIN_COVERAGE && coverageRatio <= PAYOFF_MAX_COVERAGE) {
        mode = 'payoff';
      } else if (amountCv <= FIXED_MAX_CV) {
        mode = 'fixed';
      } else {
        mode = 'partial';
      }
    } else if (payments.length >= MIN_PAYMENTS_FOR_MODE && amountCv <= FIXED_MAX_CV) {
      mode = 'fixed';
    }

    const card = cardById.get(route.cardAccountId);
    const owed = owedOf(card);
    // Never below zero: an in-flight amount larger than the reported balance
    // just means the card was already partly credited, not that it owes us.
    const inFlight = Math.max(0, Number(inFlightByCard[route.cardAccountId]) || 0);
    const owedNet = Math.max(0, owed - inFlight);
    routes.push({
      key: route.key,
      cashAccountId: route.cashAccountId,
      cardAccountId: route.cardAccountId,
      cardName: card?.name || 'Card',
      seriesKeys: route.seriesKeys,
      payments,
      paymentCount: payments.length,
      meanAmount: amounts.length ? amounts.reduce((s, v) => s + v, 0) / amounts.length : 0,
      medianIntervalDays,
      medianLagDays: median(payments.map((p) => p.lagDays)),
      lastPaymentDate: payments.length ? payments[payments.length - 1].date : null,
      cardSpend: round2(cardSpend),
      // Paired payments only, so this keeps matching what the pair matcher saw.
      totalPaid: round2(totalPaid),
      unpairedPaid: round2(unpairedPaid),
      coverageRatio,
      amountCv,
      mode,
      // `owed` stays as the card reports it — the accounts screen shows that
      // number, and it should keep matching the bank. `owedNet` is the
      // projection's figure: what will still be outstanding once the money
      // already on its way lands.
      owed,
      inFlight: round2(inFlight),
      owedNet: round2(owedNet),
    });
  });

  return routes.sort((a, b) => (b.lastPaymentDate || 0) - (a.lastPaymentDate || 0));
}

/**
 * Ledger events for upcoming card payments on payoff routes.
 *
 * Returns the events AND the series keys they cover, so the caller can drop the
 * generic recurring events for those same series instead of counting the
 * payment twice.
 *
 * Routes that aren't 'payoff' are deliberately left alone: the existing
 * mean-based recurring projection is already the right model for a fixed
 * autopay or a revolver, and inventing a balance-sized outflow for them would
 * be far worse than the error we're fixing.
 *
 * @returns {{events: Array, ownedSeriesKeys: Set<string>, routes: Array}}
 */
export function projectCardPayments(transactions, accounts, options = {}) {
  const {
    today = new Date(),
    horizonDays = 60,
    lookbackMonths = 6,
    pairs = null,
    inFlightByCard = {},
    unpairedPaymentsByCard = {},
  } = options;

  const routes = buildPaymentRoutes(transactions, accounts, {
    today, lookbackMonths, pairs, inFlightByCard, unpairedPaymentsByCard,
  });
  const start = startOfDay(today) || new Date();
  const horizonEnd = addDays(start, horizonDays);

  const events = [];
  const ownedSeriesKeys = new Set();

  routes.forEach((route) => {
    if (route.mode !== 'payoff') return;
    if (!route.lastPaymentDate || !route.medianIntervalDays) return;
    const cadence = cadenceForInterval(route.medianIntervalDays);
    if (!cadence) return;
    // Nothing owed and nothing on its way — no payment to project at all.
    if (!(route.owed > 0) && !(route.inFlight > 0)) return;

    // Walk forward from the last payment. Occurrences already in the past are
    // baked into the current cash balance, same reasoning as runway.js
    // anchoring on a series' latest instance.
    let cursor = nextOccurrence(route.lastPaymentDate, cadence);
    let first = true;
    let guard = 0;
    let emitted = 0;
    let covered = false;
    while (cursor <= horizonEnd && guard < 500) {
      guard++;
      if (cursor > start) {
        // Only the first occurrence is knowable from today's balance, and only
        // net of money already sent against it.
        const amount = first ? route.owedNet : route.meanAmount;
        const wasFirst = first;
        first = false;

        // In-flight money has covered this cycle in full. Emitting a zero
        // event would draw a payment marker for a payment that isn't coming;
        // the cash left days ago and the balance already shows it.
        if (wasFirst && !(amount > 0)) {
          covered = true;
          cursor = nextOccurrence(cursor, cadence);
          continue;
        }

        events.push({
          date: startOfDay(cursor),
          seriesKey: [...route.seriesKeys][0] || route.key,
          routeKey: route.key,
          cardAccountId: route.cardAccountId,
          description: `${route.cardName} payment`,
          type: 'expense',
          amount: round2(amount),
          delta: round2(-amount),
          basis: wasFirst ? 'balance' : 'mean',
          mode: route.mode,
          isCardPayment: true,
        });
        emitted++;
      }
      cursor = nextOccurrence(cursor, cadence);
    }

    // Claim the series whenever this route spoke for it — including the cycle
    // we deliberately left empty. Staying silent there would let the generic
    // mean-based occurrence back in and re-add the payment we just established
    // has already been made.
    if (emitted || covered) route.seriesKeys.forEach((k) => ownedSeriesKeys.add(k));
  });

  events.sort((a, b) => a.date - b.date);
  return { events, ownedSeriesKeys, routes };
}

export const _internal = {
  cadenceForInterval,
  coefficientOfVariation,
  median,
  owedOf,
  PAYOFF_MIN_COVERAGE,
  PAYOFF_MAX_COVERAGE,
  FIXED_MAX_CV,
};
